import { request } from '@/common/services/request';
import {
  getMetricLifecycleValidationError,
  normalizeMetricBatchLifecyclePatch,
} from '../utils/metric-lifecycle';

const getPayloadData = (payload) => payload?.data || payload || {};

const toProTableResult = (result) => ({
  data: result.data || [],
  success: true,
  total: result.meta?.total || 0,
});

const queryOperationTable = async (path, params) => {
  const result = await request(path, {
    method: 'GET',
    params,
  });
  return toProTableResult(result);
};

const assertMetricLifecycle = (data) => {
  const error = getMetricLifecycleValidationError(data);
  if (error) {
    throw new Error(error);
  }
};

const getMetricCreateData = (data) => {
  const result = {
    name: data.name,
    metricType: data.metricType,
    valueType: data.valueType,
    enabled: data.enabled,
    ...(data.metricType === 'atomic'
      ? {
          allocationType: data.allocationType ?? null,
          attributionType: data.attributionType || 'direct',
          periodType: data.periodType ?? null,
          statisticalModelType: data.periodType ? (data.statisticalModelType ?? null) : null,
        }
      : {}),
  };
  assertMetricLifecycle({ ...result, sourceDomain: data.sourceDomain });
  return result;
};

const getStatisticalModelUpdateData = (data) => {
  if (Object.hasOwn(data, 'periodType') && data.periodType == null) {
    return { statisticalModelType: null };
  }
  if (!Object.hasOwn(data, 'statisticalModelType')) {
    return {};
  }
  return { statisticalModelType: data.statisticalModelType ?? null };
};

const getMetricUpdateData = (data) => {
  const result = {
    enabled: data.enabled,
    name: data.name,
    ...(data.metricType === 'atomic'
      ? {
          ...(Object.hasOwn(data, 'allocationType')
            ? { allocationType: data.allocationType ?? null }
            : {}),
          ...(Object.hasOwn(data, 'attributionType')
            ? { attributionType: data.attributionType ?? null }
            : {}),
          ...(Object.hasOwn(data, 'periodType') ? { periodType: data.periodType ?? null } : {}),
          ...getStatisticalModelUpdateData(data),
        }
      : {}),
  };
  const hasCompleteLifecycle =
    data.metricType === 'atomic' &&
    data.valueType &&
    Object.hasOwn(result, 'periodType') &&
    (data.valueType === 'ratio' || Object.hasOwn(result, 'allocationType'));
  if (hasCompleteLifecycle) {
    assertMetricLifecycle({
      ...result,
      metricType: data.metricType,
      sourceDomain: data.sourceDomain,
      valueType: data.valueType,
    });
  }
  return result;
};

const getCompositeMetricExpressionData = (data) => ({
  calculationMode: data.calculationMode || 'post_aggregate',
  enabled: data.enabled,
  expression: data.expression,
  metricId: data.metricId,
});

const getFactViewSectionData = (data) => ({
  enabled: data.enabled,
  sortOrder: data.sortOrder,
  title: data.title,
  viewKey: data.viewKey,
});

const getFactViewMetricData = (data) => ({
  enabled: data.enabled,
  metricId: data.metricId,
  role: data.role,
  sectionId: data.sectionId,
  sortOrder: data.sortOrder,
});

const getFactViewSectionMetricsData = (data) => ({
  metrics: data.metrics,
});

const getAtomicMetricMappingData = (data) => ({
  enabled: data.enabled,
  metricId: data.metricId,
  sourceDomain: data.sourceDomain,
  ...(data.sourceDomain === 'expense'
    ? {
        expenseTypeLevel1: data.expenseTypeLevel1,
        expenseTypeLevel2: data.expenseTypeLevel2,
        expenseTypeLevel3: data.expenseTypeLevel3,
      }
    : {}),
  ...(data.sourceDomain === 'income'
    ? {
        incomeTypeLevel1: data.incomeTypeLevel1,
        incomeTypeLevel2: data.incomeTypeLevel2,
        incomeTypeLevel3: data.incomeTypeLevel3,
        incomeValueType: data.incomeValueType,
      }
    : {}),
  ...(data.sourceDomain === 'salary'
    ? {
        salaryTypeLevel1: data.salaryTypeLevel1,
        salaryTypeLevel2: data.salaryTypeLevel2,
        salaryTypeLevel3: data.salaryTypeLevel3,
        salaryValueType: data.salaryValueType,
      }
    : {}),
  ...(data.sourceDomain === 'plan'
    ? {
        planItemName: data.planItemName,
        planValueType: data.planValueType,
      }
    : {}),
});

export const queryOperationMetrics = (params) =>
  queryOperationTable('/api/operations/tenant/metrics', params);

export const queryAtomicMetricMappings = (params) =>
  queryOperationTable('/api/operations/tenant/metric-mappings', params);

export const queryAtomicMetricMappingsWithMeta = async (params) => {
  const result = await request('/api/operations/tenant/metric-mappings', {
    method: 'GET',
    params,
  });
  return {
    ...toProTableResult(result),
    meta: result.meta || {},
  };
};

export const queryCompositeMetricExpressions = (params) =>
  queryOperationTable('/api/operations/tenant/metric-rules', params);

export const queryFactViewSections = (params) =>
  queryOperationTable('/api/operations/tenant/fact-view-sections', params);

export const queryFactViewMetrics = (params) =>
  queryOperationTable('/api/operations/tenant/fact-view-metrics', params);

export const queryExpenseEvents = (params) =>
  queryOperationTable('/api/operations/tenant/expense-events', params);

export const queryIncomeEvents = (params) =>
  queryOperationTable('/api/operations/tenant/income-events', params);

export const querySalaryEvents = (params) =>
  queryOperationTable('/api/operations/tenant/salary-events', params);

export const queryPaymentEvents = (params) =>
  queryOperationTable('/api/operations/tenant/payment-events', params);

export const queryPlanEvents = (params) =>
  queryOperationTable('/api/operations/tenant/plan-events', params);

export const queryOperationStores = async (params) => {
  const result = await request('/api/operations/tenant/stores', {
    method: 'GET',
    params,
  });
  const data = result.data || {};
  return {
    data: data.stores || [],
    success: true,
    total: result.meta?.total || 0,
    aliasHealth: data.aliasHealth || {
      duplicateNames: [],
      unmatchedNames: [],
    },
    meta: result.meta || {},
  };
};

export const queryStoreNameIssues = async (params) => {
  const result = await request('/api/operations/tenant/store-name-issues', {
    method: 'GET',
    params,
  });
  return {
    ...toProTableResult(result),
    meta: result.meta || {},
  };
};

export const resolveStoreNameIssues = ({ action, names, organizationId, storeId }) =>
  request('/api/operations/tenant/store-name-issues', {
    method: 'PATCH',
    params: { organizationId },
    data: {
      action,
      names,
      ...(storeId ? { storeId } : {}),
    },
  });

export async function queryOperationEntities(params) {
  const result = await request('/api/operations/tenant/operation-entities', {
    method: 'GET',
    params,
  });
  return result.data || [];
}

export const queryOperationEntityPrincipalMatches = async (params) => {
  const result = await request('/api/operations/tenant/operation-entity-principal-matches', {
    method: 'GET',
    params,
  });
  return result.data;
};

export const confirmOperationEntityPrincipalMatches = (payload) => {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/operation-entity-principal-matches/confirm', {
    method: 'POST',
    params: { organizationId: data.organizationId },
    data: {
      applyMode: data.applyMode,
      collectionId: data.collectionId,
      items: data.items,
      scope: data.scope,
      stateToken: data.stateToken,
    },
  });
};

export async function updateOperationStoreAliases(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/stores', {
    method: 'PATCH',
    params: {
      organizationId: data.organizationId,
      storeId: data.id,
    },
    data: { aliases: data.aliases },
  });
}

export async function createOperationEntity(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/operation-entities', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: {
      parentId: data.parentId,
      code: data.code,
      name: data.name,
      memberIds: data.memberIds,
    },
  });
}

export async function updateOperationEntity(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/operation-entities', {
    method: 'PATCH',
    params: {
      organizationId: data.organizationId,
      operationEntityId: data.id,
    },
    data: {
      parentId: data.parentId,
      name: data.name,
      enabled: data.enabled,
      collectionId: data.collectionId,
      externalUserIds: data.externalUserIds,
      memberIds: data.memberIds,
    },
  });
}

export async function deleteOperationEntity(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/operation-entities', {
    method: 'DELETE',
    params: {
      operationEntityId: data.id,
      organizationId: data.organizationId,
    },
  });
}

export async function batchUpdateOperationEntities(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/operation-entities/batch', {
    method: 'PATCH',
    params: { organizationId: data.organizationId },
    data: {
      action: data.action,
      entityIds: data.entityIds,
      ...(data.parentId ? { parentId: data.parentId } : {}),
      ...(typeof data.enabled === 'boolean' ? { enabled: data.enabled } : {}),
    },
  });
}

export async function createOperationMetric(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/metrics', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: getMetricCreateData(data),
  });
}

export async function updateOperationMetric(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/metrics', {
    method: 'PATCH',
    params: {
      organizationId: data.organizationId,
      metricId: data.id,
    },
    data: getMetricUpdateData(data),
  });
}

export async function batchUpdateOperationMetricConfigurations(payload) {
  const data = getPayloadData(payload);
  const lifecycle = normalizeMetricBatchLifecyclePatch(data);
  return request('/api/operations/tenant/metrics/batch', {
    method: 'PATCH',
    params: {
      organizationId: data.organizationId,
    },
    data: {
      metricIds: data.metricIds,
      ...(Object.hasOwn(data, 'attributionType') ? { attributionType: data.attributionType } : {}),
      ...lifecycle,
    },
  });
}

export async function removeOperationMetric(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/metrics', {
    method: 'DELETE',
    params: {
      organizationId: data.organizationId,
      metricId: data.id,
    },
  });
}

export async function createAtomicMetricForMapping(payload) {
  const data = getPayloadData(payload);
  const result = await createOperationMetric({
    data: {
      allocationType: data.allocationType,
      attributionType: data.attributionType,
      enabled: data.enabled,
      metricType: 'atomic',
      name: data.name,
      organizationId: data.organizationId,
      periodType: data.periodType,
      statisticalModelType: data.statisticalModelType,
      valueType: data.valueType,
    },
  });
  const metric = result?.data || result;
  if (!metric?.id) {
    throw new Error('原子指标创建结果缺少指标 ID');
  }
  try {
    return await saveAtomicMetricMapping({
      data: {
        ...data,
        metricId: metric.id,
      },
    });
  } catch {
    try {
      return await saveAtomicMetricMapping({
        data: {
          ...data,
          metricId: metric.id,
        },
      });
    } catch (error) {
      try {
        await removeOperationMetric({
          data: {
            id: metric.id,
            organizationId: data.organizationId,
          },
        });
      } catch (cleanupError) {
        if (cleanupError?.response?.status === 409 || cleanupError?.status === 409) {
          return { data: { metricId: metric.id } };
        }
      }
      throw error;
    }
  }
}

export async function createCompositeMetricExpression(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/metric-rules', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: getCompositeMetricExpressionData(data),
  });
}

export async function saveAtomicMetricMapping(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/metric-mappings', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: getAtomicMetricMappingData(data),
  });
}

export async function autoConfigureAtomicMetricMappings(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/metric-mappings/auto-configure', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: {
      sourceDomain: data.sourceDomain,
    },
  });
}

export async function autoConfigureCompositeMetricExpressions(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/metric-rules/auto-configure', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
  });
}

export async function removeAtomicMetricMapping(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/metric-mappings', {
    method: 'DELETE',
    params: {
      mappingId: data.id,
      organizationId: data.organizationId,
    },
  });
}

export async function createFactViewSection(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/fact-view-sections', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: getFactViewSectionData(data),
  });
}

export async function updateCompositeMetricExpression(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/metric-rules', {
    method: 'PATCH',
    params: {
      organizationId: data.organizationId,
      ruleId: data.id,
    },
    data: getCompositeMetricExpressionData(data),
  });
}

export async function createFactViewMetric(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/fact-view-metrics', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: getFactViewMetricData(data),
  });
}

export async function updateFactViewSection(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/fact-view-sections', {
    method: 'PATCH',
    params: {
      organizationId: data.organizationId,
      sectionId: data.id,
    },
    data: getFactViewSectionData(data),
  });
}

export async function removeFactViewSection(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/fact-view-sections', {
    method: 'DELETE',
    params: {
      organizationId: data.organizationId,
      sectionId: data.id,
    },
  });
}

export async function updateFactViewMetric(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/fact-view-metrics', {
    method: 'PATCH',
    params: {
      organizationId: data.organizationId,
      viewMetricId: data.id,
    },
    data: getFactViewMetricData(data),
  });
}

export async function saveFactViewSectionMetrics(payload) {
  const data = getPayloadData(payload);
  return request('/api/operations/tenant/fact-view-section-metrics', {
    method: 'PUT',
    params: {
      organizationId: data.organizationId,
      sectionId: data.sectionId,
    },
    data: getFactViewSectionMetricsData(data),
  });
}
